import React from "react";
import sumBy from "lodash/sumBy";
import pluralize from "pluralize";

import {
  formatNumber,
  formatNumberWithThreshold,
  formatPercent,
} from "utils/format";
import Badge from "ui/Badge";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelTable from "ui/PanelTable";

import TagValue from "./TagValue";
import { useQueryTagOverviewQuery } from "./gql/Query.generated";
import styles from "./style.module.scss";
import { QueryTagSummary } from "types/types";

interface Props {
  organizationSlug: string;
  databaseId: string;
  tags: QueryTagSummary[];
}

const QueryTagOverview = ({ organizationSlug, databaseId, tags }: Props) => {
  const { data, loading, error } = useQueryTagOverviewQuery({
    variables: {
      organizationSlug,
      databaseId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const serverId = data.getServerDetails.humanId;
  const { githubRepository, githubBranch } = data.getOrganizationDetails;

  const secondaryTitle = (
    <a href="https://pganalyze.com/docs/query-performance/query-tags">
      Learn more
    </a>
  );

  return (
    <Panel title={<span>Query Tags</span>} secondaryTitle={secondaryTitle}>
      <PanelTable borders>
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th className={styles.samplesColumnHeader}>% of All Samples</th>
          </tr>
        </thead>
        <tbody>
          {tags.map((summary) => {
            const sampleCount = sumBy(summary.values, (v) => v.count);
            let values = summary.values;
            if (values.length > 5) {
              const otherLength = values.slice(4).length;
              const otherSampleCount = sumBy(values.slice(4), (v) => v.count);
              values = summary.values.slice(0, 4);
              values.push({
                __typename: null,
                value: `${otherLength} other values…`,
                count: otherSampleCount,
              });
            }

            return values.map((value, idx) => {
              const badgeTitle =
                summary.key == "role"
                  ? `${formatNumberWithThreshold(
                      value.count,
                      2,
                    )} of ${formatNumber(sampleCount, 2)} calls / min`
                  : `${value.count} of ${sampleCount} total ${pluralize(
                      "sample",
                      sampleCount,
                      false,
                    )}`;

              return (
                <tr key={summary.key + value.value}>
                  {idx == 0 && <td rowSpan={values.length}>{summary.key}</td>}
                  <td className={styles.valueColumn}>
                    <TagValue
                      serverId={serverId}
                      databaseId={databaseId}
                      tagKey={summary.key}
                      tagValue={value.value}
                      tagId={value.id}
                      githubRepository={githubRepository}
                      githubBranch={githubBranch}
                    />
                  </td>
                  <td className={styles.samplesColumn}>
                    {formatPercent(value.count / sampleCount, 1)}
                    <br />
                    <Badge title={badgeTitle}>
                      {summary.key == "role"
                        ? `${formatNumberWithThreshold(
                            value.count,
                            2,
                          )} calls / min`
                        : pluralize("sample", value.count, true)}
                    </Badge>
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </PanelTable>
    </Panel>
  );
};

export default QueryTagOverview;
